// Datenzugriffe der E-Signatur (Liste, Dialoge, Aktenschrank) – Verhalten wie in der bisherigen Signatur-Liste.

import { supabase } from '../supabase/client';
import { ladeDateiHoch } from './aktenschrankApi';
import { zwischenstandDateiname } from '../signatur/vorgangAktionen';
import { istPdfVerschluesselt, PDF_GESCHUETZT_MELDUNG } from '../signatur/pdfSchutz';
import type { SignaturVorgang, Unterzeichner } from './signaturTypen';
import { ladePdfjs } from '../pdf/pdfjs';

const BUCKET = 'signaturen';

export async function ladeSignaturVorgaenge(): Promise<SignaturVorgang[]> {
  const { data, error } = await supabase.from('signatur_vorgaenge').select('*').order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as SignaturVorgang[];
}

/**
 * Vorschaubild (erste Seite) laden – fehlt es, wird es selbstheilend erzeugt (pdfjs → JPEG in den Bucket).
 * Abgeschlossene Vorgänge zeigen das SIGNIERTE PDF (Suffix -signiert.jpg).
 */
export async function ladeVorschaubild(v: SignaturVorgang): Promise<string | null> {
  const brauchtSigniert = v.status === 'abgeschlossen' && !!v.signiert_path && !(v.vorschau_path ?? '').endsWith('-signiert.jpg');
  if (v.vorschau_path && !brauchtSigniert) {
    const { data } = await supabase.storage.from(BUCKET).download(v.vorschau_path);
    return data ? URL.createObjectURL(data) : null;
  }
  const quelle = brauchtSigniert ? (v.signiert_path as string) : v.original_path;
  const { data: pdfBlob } = await supabase.storage.from(BUCKET).download(quelle);
  if (!pdfBlob) return null;
  const pdfjs = await ladePdfjs();
  const doc = await pdfjs.getDocument({ data: await pdfBlob.arrayBuffer() }).promise;
  const seite = await doc.getPage(1);
  const viewport = seite.getViewport({ scale: 420 / seite.getViewport({ scale: 1 }).width });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  await seite.render({ canvasContext: ctx, viewport, canvas }).promise;
  const jpeg: Blob | null = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.85));
  void doc.destroy();
  if (!jpeg) return null;
  const pfad = brauchtSigniert ? `vorschau/${v.id}-signiert.jpg` : `vorschau/${v.id}.jpg`;
  const { error: upErr } = await supabase.storage.from(BUCKET).upload(pfad, jpeg, { contentType: 'image/jpeg', upsert: true });
  if (!upErr) {
    await supabase.from('signatur_vorgaenge').update({ vorschau_path: pfad }).eq('id', v.id);
    if (brauchtSigniert && v.vorschau_path && v.vorschau_path !== pfad) void supabase.storage.from(BUCKET).remove([v.vorschau_path]);
  }
  return URL.createObjectURL(jpeg);
}

/**
 * Neuer Entwurf als Kopie (aus Vorlage oder abgebrochenem Vorgang): Storage-Dateien kopieren, Zeile klonen.
 * Aus Vorlagen bleiben nur die Rollen-Namen (E-Mails leer), beim Kopieren eines Vorgangs auch die E-Mails.
 */
export async function kopiereAlsEntwurf(quelle: SignaturVorgang, opt: { mitEmail: boolean; userId: string | null }): Promise<SignaturVorgang> {
  const neueId = crypto.randomUUID();
  const neuesOriginal = `original/${neueId}.pdf`;
  const { error: copyErr } = await supabase.storage.from(BUCKET).copy(quelle.original_path, neuesOriginal);
  if (copyErr) throw new Error(`PDF-Kopie fehlgeschlagen: ${copyErr.message}`);
  let neueVorschau: string | null = null;
  if (quelle.vorschau_path && !quelle.vorschau_path.endsWith('-signiert.jpg')) {
    neueVorschau = `vorschau/${neueId}.jpg`;
    const { error } = await supabase.storage.from(BUCKET).copy(quelle.vorschau_path, neueVorschau);
    if (error) neueVorschau = null; // optional – Selbstheilung übernimmt
  }
  const unterzeichner: Unterzeichner[] = (quelle.unterzeichner ?? []).map(u => ({
    name: u.name, email: opt.mitEmail ? u.email : '', token: '', status: 'offen', signiert_am: null, ip: null, werte: {}, unterschriften: {},
    ...(u.quelle ? { quelle: u.quelle } : {}),
  }));
  const { data, error } = await supabase.from('signatur_vorgaenge').insert({
    titel: quelle.titel, status: 'entwurf', ist_vorlage: false, original_path: neuesOriginal, vorschau_path: neueVorschau,
    seiten: quelle.seiten, felder: quelle.felder, unterzeichner, created_by: opt.userId,
    vorlage_kategorie: quelle.vorlage_kategorie ?? null,
    ...(opt.mitEmail ? { bezug: quelle.bezug ?? {}, vorbelegung: quelle.vorbelegung ?? {} } : {}),
  }).select('*').single();
  if (error || !data) throw new Error(error?.message || 'Vorgang konnte nicht angelegt werden.');
  return data as SignaturVorgang;
}

/** Vorgang samt Storage-Artefakten löschen (Original, Vorschau, signiertes PDF, Unterschrift-PNGs). */
export async function loescheVorgang(v: SignaturVorgang): Promise<void> {
  const { error } = await supabase.from('signatur_vorgaenge').delete().eq('id', v.id);
  if (error) throw new Error(error.message);
  const pfade = [v.original_path, v.vorschau_path, v.signiert_path].filter((p): p is string => !!p);
  const { data: unterschriften } = await supabase.storage.from(BUCKET).list(`unterschriften/${v.id}`);
  for (const f of unterschriften ?? []) pfade.push(`unterschriften/${v.id}/${f.name}`);
  if (pfade.length) await supabase.storage.from(BUCKET).remove(pfade);
}

/** Abbrechen: Signier-Links prüfen den Status „verschickt“ – danach sind sie ungültig. */
export async function brecheVorgangAb(id: string): Promise<void> {
  const { error } = await supabase.from('signatur_vorgaenge').update({ status: 'abgebrochen', updated_at: new Date().toISOString() }).eq('id', id).eq('status', 'verschickt');
  if (error) throw new Error(error.message);
}

async function signieren<T extends { ok?: boolean; fehler?: string }>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('signieren', { body });
  const d = (data ?? {}) as T;
  if (error || !d.ok) throw new Error(d.fehler || error?.message || 'Unbekannter Fehler');
  return d;
}

export const verschickeVorgang = (vorgangId: string) => signieren({ aktion: 'versenden', vorgangId });
export const sendeErneut = (vorgangId: string) => signieren<{ ok: boolean; name?: string; email?: string }>({ aktion: 'erneut-senden', vorgangId });

/** Zwischenstand als PDF holen (#600: Anzeige in der App, Herunterladen von dort). */
export async function holeZwischenstand(v: SignaturVorgang): Promise<{ blob: Blob; name: string }> {
  const d = await signieren<{ ok: boolean; url?: string }>({ aktion: 'zwischenstand', vorgangId: v.id });
  if (!d.url) throw new Error('Kein Download-Link erhalten.');
  const res = await fetch(d.url);
  if (!res.ok) throw new Error('Zwischenstand konnte nicht geladen werden.');
  return { blob: await res.blob(), name: zwischenstandDateiname(v.titel) };
}


export async function oeffneSigniertesPdf(v: SignaturVorgang): Promise<void> {
  if (!v.signiert_path) throw new Error('Kein fertiges Dokument vorhanden.');
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(v.signiert_path, 300);
  if (error || !data?.signedUrl) throw new Error(error?.message || 'Kein signierter Link erhalten.');
  window.open(data.signedUrl, '_blank', 'noopener');
}

/** „Von Hand abgeschlossen“: optional gegengezeichnetes PDF (fester Pfad manuell/<id>.pdf), dann Abschluss. */
export async function schliesseVonHandAb(v: SignaturVorgang, notiz: string, datei: File | null): Promise<void> {
  if (datei) {
    const { error } = await supabase.storage.from(BUCKET).upload(`manuell/${v.id}.pdf`, datei, { contentType: 'application/pdf', upsert: true });
    if (error) throw new Error(`Hochladen fehlgeschlagen: ${error.message}`);
  }
  await signieren({ aktion: 'von-hand-abschliessen', vorgangId: v.id, notiz, mitPdf: !!datei });
}

/** Fertiges PDF in einen Aktenschrank-Ordner hochladen und den Vorgang als abgelegt stempeln. */
export async function legeSigniertInAkte(v: SignaturVorgang, ordnerId: string): Promise<{ dokumentId: string | null; fileId: string }> {
  if (!v.signiert_path) throw new Error('Kein fertiges Dokument vorhanden.');
  const { data: blob, error } = await supabase.storage.from(BUCKET).download(v.signiert_path);
  if (error || !blob) throw new Error(error?.message || 'PDF konnte nicht geladen werden.');
  const r = await ladeDateiHoch(ordnerId, new File([blob], `${v.titel.trim() || 'Dokument'}.pdf`, { type: 'application/pdf' }));
  const { error: stempel } = await supabase.from('signatur_vorgaenge').update({ abgelegt_am: new Date().toISOString() }).eq('id', v.id);
  if (stempel) console.error('abgelegt_am-Stempel fehlgeschlagen:', stempel.message);
  return r;
}

export function speichereBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * Neuer Signatur-Entwurf aus einer vorhandenen PDF (z. B. „Zur Signatur“ im Aktenschrank): gleiche Prüfungen wie
 * der Upload im Editor (max. 20 MB, kein Schreibschutz), Original in den Bucket, Entwurf anlegen.
 */
export async function legeEntwurfAusPdfAn(pdf: Blob, titel: string, userId: string | null): Promise<string> {
  if (pdf.size > 20 * 1024 * 1024) throw new Error('Die Datei ist zu groß (max. 20 MB).');
  const bytes = new Uint8Array(await pdf.arrayBuffer());
  if (istPdfVerschluesselt(bytes)) throw new Error(PDF_GESCHUETZT_MELDUNG);
  const pdfjs = await ladePdfjs();
  const doc = await pdfjs.getDocument({ data: bytes.slice() }).promise;
  const seiten = doc.numPages;
  void doc.destroy();
  const pfad = `original/${crypto.randomUUID()}.pdf`;
  const { error: upErr } = await supabase.storage.from(BUCKET).upload(pfad, new Blob([bytes], { type: 'application/pdf' }), { contentType: 'application/pdf' });
  if (upErr) throw new Error(upErr.message);
  const { data, error } = await supabase.from('signatur_vorgaenge').insert({
    titel: titel.replace(/\.pdf$/i, '').trim() || 'Dokument', status: 'entwurf', original_path: pfad, seiten, felder: [], unterzeichner: [], created_by: userId,
  }).select('id').single();
  if (error || !data) throw new Error(error?.message || 'Vorgang konnte nicht angelegt werden.');
  return data.id as string;
}
